/* View once media shows on the web as a locked card.
 * The web never opens, plays, downloads or forwards it. */

export const VIEW_ONCE_MESSAGE_TYPES = ['audio', 'image', 'video'];

const VIEW_ONCE_ON_VALUES = ['1', 'true'];

// Keep identical to the SDK copy in helpers/utils.js: IndexedDB rows predate the SDK flag.
/**
 * Normalizes a raw view once flag to 1 (on) or 0 (off).
 *
 * Only 1, "1", true and "true" (any case) are on. iOS always sends "0", which is truthy
 * in JS, so a plain truthiness check would lock every iOS media message.
 *
 * @param value Raw flag as the SDK, the server or IndexedDB hands it over.
 * @returns 1 when view once is on, otherwise 0.
 */
export const normalizeViewOnce = value => {
    if (value === 1 || value === true) return 1;
    if (typeof value !== 'string') return 0;
    return VIEW_ONCE_ON_VALUES.includes(value.trim().toLowerCase()) ? 1 : 0;
};

/**
 * Tells whether a message must render as a locked view once card.
 *
 * Reads both shapes in use: conversation and recent chat messages keep the type in msgBody,
 * the SDK media list keeps it at the top level.
 *
 * @param message Message object.
 * @returns Whether the message is view once audio, image or video.
 */
export const isViewOnceMessage = message => {
    if (!message || normalizeViewOnce(message.viewOnce) !== 1) return false;
    const messageType = message.msgBody?.message_type || message.message_type || message.msgType;
    return VIEW_ONCE_MESSAGE_TYPES.includes(messageType);
};

/**
 * Collects the ids of view once messages in a loaded list.
 *
 * The server media list may report the flag as 0 for older items, so callers also exclude
 * any id the loaded thread already knows as view once.
 *
 * @param messages Messages of the loaded thread.
 * @returns Set of view once message ids.
 */
export const getViewOnceMessageIds = (messages = []) =>
    new Set(messages.filter(isViewOnceMessage).map(message => message.msgId));
